import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Copy, Trash2 } from "lucide-react";
import {
  ActivityShareLink,
  ActivityShareOptions,
  ShareActivityType,
  createActivityShare,
  listActivityShares,
  revokeActivityShare,
} from "../../utils/api";

interface ShareActivityDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  activityType: ShareActivityType;
  activityId: string;
}

const OPTION_LABELS: { key: keyof ActivityShareOptions; label: string }[] = [
  { key: "showDogName", label: "Nom du chien" },
  { key: "showMap", label: "Carte et tracés" },
  { key: "showWeather", label: "Météo" },
  { key: "showNotes", label: "Notes" },
  { key: "showTrainerComment", label: "Commentaire du moniteur" },
];

const buildUrl = (token: string, embed = false) =>
  `${window.location.origin}/share/${token}${embed ? "?embed=1" : ""}`;

export const buildEmbedCode = (token: string) =>
  `<iframe src="${buildUrl(token, true)}" width="100%" height="600" style="border:0" loading="lazy" title="Activité"></iframe>`;

export function ShareActivityDialog({ isOpen, onOpenChange, activityType, activityId }: ShareActivityDialogProps) {
  const [options, setOptions] = useState<ActivityShareOptions>({
    showDogName: true,
    showMap: true,
    showWeather: true,
    showNotes: false,
    showTrainerComment: false,
  });
  const [expiresIn, setExpiresIn] = useState<"24h" | "7d" | "30d" | "never">("7d");
  const [links, setLinks] = useState<ActivityShareLink[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLinks(await listActivityShares(activityType, activityId));
    } catch {
      toast.error("Impossible de charger les liens de partage.");
    }
  }, [activityType, activityId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      toast.error("Impossible de copier.");
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const link = await createActivityShare({ activityType, activityId, expiresIn, options });
      setLinks((current) => [link, ...current.filter((item) => item.token !== link.token)]);
      toast.success("Lien créé.");
    } catch {
      toast.error("Impossible de créer le lien.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token: string) => {
    try {
      await revokeActivityShare(token);
      setLinks((prev) => prev.filter((l) => l.token !== token));
    } catch {
      toast.error("Impossible de révoquer le lien.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Partager l'activité</DialogTitle>
          <DialogDescription>
            Créez un lien public (lecture seule) ou un code à intégrer dans une page web.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Contenu partagé</legend>
            {OPTION_LABELS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!options[key]}
                  onChange={(e) => setOptions({ ...options, [key]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <label className="flex items-center gap-2 text-sm">
            Expiration
            <select
              className="rounded border px-2 py-1"
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value as typeof expiresIn)}
            >
              <option value="24h">24 heures</option>
              <option value="7d">7 jours</option>
              <option value="30d">30 jours</option>
              <option value="never">Jamais</option>
            </select>
          </label>
          <Button onClick={create} disabled={busy}>Créer un lien</Button>
        </div>

        {links.length > 0 && (
          <ul className="space-y-3 max-h-64 overflow-auto">
            {links.map((link) => (
              <li key={link.token} className="rounded border p-2 text-sm space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{buildUrl(link.token)}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" aria-label="Copier le lien" onClick={() => copy(buildUrl(link.token), "Lien copié")}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" aria-label="Révoquer le lien" onClick={() => revoke(link.token)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {link.expiresAt ? `Expire le ${new Date(link.expiresAt).toLocaleString("fr-FR")}` : "N'expire jamais"}
                </div>
                <Button size="sm" variant="secondary" onClick={() => copy(buildEmbedCode(link.token), "Code d'intégration copié")}>
                  Copier le code d'intégration
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
